'use client';

import Image from 'next/image';
import { cn } from '~/lib/utils';
import { Button } from '~/components/ui/button';
import { useState, useEffect, useRef, useCallback } from 'react';

const images = [
  '/static/images/hero-one.jpg',
  '/static/images/hero-two.jpg',
  '/static/images/hero-three.jpg',
  '/static/images/hero-four.jpg',
  '/static/images/hero-five.jpg',
];

const DRAG_THRESHOLD_RATIO = 0.15;

const ImageSlider = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const directionRef = useRef(1);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const sliderContainerRef = useRef<HTMLDivElement>(null);
  const imageTrackRef = useRef<HTMLDivElement>(null);

  const [isPointerDown, setIsPointerDown] = useState(false);
  const dragStartPosRef = useRef(0);
  const dragInitialTrackPosRef = useRef(0);
  const currentTrackPosRef = useRef(0);

  const stopSlider = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
  }, []);

  const startSlider = useCallback(() => {
    stopSlider();
    intervalRef.current = setInterval(() => {
      setCurrentSlide((prev) => {
        const nextPotentialSlide = prev + directionRef.current;
        if (nextPotentialSlide >= images.length || nextPotentialSlide < 0) {
          directionRef.current *= -1;
          return prev + directionRef.current;
        }
        return nextPotentialSlide;
      });
    }, 5000);
  }, [stopSlider]);

  useEffect(() => {
    startSlider();
    return () => stopSlider();
  }, [startSlider, stopSlider]);

  useEffect(() => {
    const sliderEl = sliderContainerRef.current;
    const trackEl = imageTrackRef.current;

    if (!isPointerDown || !sliderEl || !trackEl) {
      return;
    }

    const controller = new AbortController();

    const handlePointerMove = (event: MouseEvent | TouchEvent) => {
      const currentX =
        'touches' in event ? event.touches[0].clientX : event.clientX;
      const diffX = currentX - dragStartPosRef.current;
      const sliderWidth = sliderEl.offsetWidth;
      const diffPercentage = (diffX / sliderWidth) * 100;

      if (
        (currentSlide === 0 && diffPercentage > 0) ||
        (currentSlide === images.length - 1 && diffPercentage < 0)
      ) {
        return;
      }

      const newTrackPos = dragInitialTrackPosRef.current + diffPercentage;
      currentTrackPosRef.current = newTrackPos;
      trackEl.style.transform = `translateX(${newTrackPos}%)`;
    };

    const handlePointerUp = () => {
      setIsPointerDown(false);

      const dragDistance =
        currentTrackPosRef.current - dragInitialTrackPosRef.current;
      const threshold = 100 * DRAG_THRESHOLD_RATIO;

      const previousSlide = currentSlide;
      let newSlide = currentSlide;

      if (Math.abs(dragDistance) > threshold) {
        if (dragDistance < 0) {
          newSlide = Math.min(currentSlide + 1, images.length - 1);
        } else {
          newSlide = Math.max(currentSlide - 1, 0);
        }
      }

      if (trackEl) {
        trackEl.style.transition = 'transform 0.5s ease-in-out';
        trackEl.style.transform = `translateX(${-newSlide * 100}%)`;
      }

      setCurrentSlide(newSlide);

      if (newSlide !== previousSlide) {
        directionRef.current = newSlide > previousSlide ? 1 : -1;
      }

      startSlider();
    };

    document.addEventListener('mousemove', handlePointerMove, {
      signal: controller.signal,
    });
    document.addEventListener('mouseup', handlePointerUp, {
      signal: controller.signal,
    });
    document.addEventListener('touchmove', handlePointerMove, {
      passive: true,
      signal: controller.signal,
    });
    document.addEventListener('touchend', handlePointerUp, {
      signal: controller.signal,
    });

    return () => {
      controller.abort();
    };
  }, [isPointerDown, currentSlide, startSlider]);

  const handleIndicatorClick = (index: number) => {
    if (isPointerDown) return;
    stopSlider();
    setCurrentSlide(index);
    directionRef.current =
      index > currentSlide
        ? 1
        : index < currentSlide
          ? -1
          : directionRef.current;
    startSlider();
  };

  const handlePointerDown = (event: React.MouseEvent | React.TouchEvent) => {
    event.preventDefault();
    stopSlider();

    if (sliderContainerRef.current && imageTrackRef.current) {
      imageTrackRef.current.style.transition = 'none';

      const clientX =
        'touches' in event ? event.touches[0].clientX : event.clientX;
      dragStartPosRef.current = clientX;

      const initialTrackPercentage = -currentSlide * 100;
      dragInitialTrackPosRef.current = initialTrackPercentage;
      currentTrackPosRef.current = initialTrackPercentage;

      imageTrackRef.current.style.transform = `translateX(${initialTrackPercentage}%)`;
    }

    setIsPointerDown(true);
  };

  return (
    <div className="w-full flex flex-col gap-6">
      <div
        ref={sliderContainerRef}
        className="relative aspect-16/9 rounded-2xl sm:rounded-3xl overflow-hidden border border-shark-200 shadow-sm cursor-grab"
        onMouseDown={handlePointerDown}
        onTouchStart={handlePointerDown}
      >
        <div
          ref={imageTrackRef}
          className="flex"
          style={{
            transform: `translateX(-${currentSlide * 100}%)`,
            transition: isPointerDown ? 'none' : 'transform 0.5s ease-in-out',
          }}
        >
          {images.map((src, index) => (
            <div
              key={src}
              className="relative w-full flex-shrink-0 aspect-16/9"
            >
              <Image
                src={src}
                alt={`Slide ${index + 1}`}
                fill
                className="object-cover"
                priority={index === 0}
                draggable="false"
              />
            </div>
          ))}
        </div>
      </div>
      <div className="flex self-center justify-center items-center gap-2">
        {images.map((_, index) => (
          <Button
            size="icon"
            key={index}
            variant="ghost"
            className={cn(
              'h-1 p-0 min-w-0 rounded-full transition-all duration-500 ease-in-out cursor-pointer',
              index === currentSlide
                ? 'bg-blazeOrange-600 w-16'
                : 'bg-shark-200 w-6',
            )}
            onClick={() => handleIndicatorClick(index)}
          />
        ))}
      </div>
    </div>
  );
};

export default ImageSlider;
